/**
 * Real-time Notification Hook
 * Connects to SSE endpoint and handles incoming notifications
 */
import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';

export const useNotifications = () => {
  const { token, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const maxReconnectAttempts = 5;
  const reconnectAttemptsRef = useRef(0);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      cleanup();
      return;
    }

    // Calculate token expiration time
    const getTokenExpiration = (token) => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp * 1000; // Convert to milliseconds
      } catch (error) {
        console.error('Failed to parse token:', error);
        return null;
      }
    };

    const connectToNotifications = () => {
      try {
        // Prevent multiple simultaneous connection attempts
        if (eventSourceRef.current && eventSourceRef.current.readyState === EventSource.CONNECTING) {
          console.log('⏳ Connection already in progress, skipping...');
          return;
        }

        // Close existing connection if any
        if (eventSourceRef.current) {
          console.log('🔌 Closing existing connection...');
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }

        console.log('🔔 Connecting to notification stream...');
        console.log('🔔 Reconnect attempt:', reconnectAttemptsRef.current);
        console.log('🔔 API_BASE_URL:', API_BASE_URL);

        // Create EventSource with auth token in URL (EventSource doesn't support custom headers)
        // Construct URL properly to avoid malformed URLs
        const baseUrl = API_BASE_URL || window.location.origin;
        const streamPath = '/api/notifications/stream';
        const url = `${baseUrl}${streamPath}?token=${encodeURIComponent(token)}`;

        console.log('🔔 SSE URL:', url);
        console.log('🔔 Token (first 20 chars):', token.substring(0, 20) + '...');

        const eventSource = new EventSource(url);

        eventSource.onopen = () => {
          console.log('✅ Notification stream connected');
          setIsConnected(true);
          reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📨 Notification received:', data);

            if (data.type === 'connected') {
              console.log('✅ Connection confirmed:', data.message);
              return;
            }

            // Add notification to state
            const notification = {
              id: Date.now(),
              timestamp: new Date(),
              ...data
            };

            setNotifications(prev => [notification, ...prev].slice(0, 50)); // Keep last 50

            // Show toast notification
            if (data.type === 'RESERVATION_APPROACHING_MAX') {
              toast((t) => (
                <div className="flex items-start justify-between w-full">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <p className="font-medium text-yellow-900">{data.message}</p>
                      {data.customerName && (
                        <p className="text-sm text-yellow-800 mt-1">Customer: {data.customerName}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="ml-4 text-yellow-700 hover:text-yellow-900 flex-shrink-0"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ), {
                duration: 8000,
                style: {
                  background: '#FEF3C7',
                  border: '2px solid #F59E0B',
                  padding: '16px',
                  minWidth: '350px',
                  maxWidth: '500px',
                },
              });
            } else if (data.type === 'RESERVATION_EXCEEDING_MAX') {
              toast((t) => (
                <div className="flex items-start justify-between w-full">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">🚨</span>
                    <div>
                      <p className="font-semibold text-red-900">{data.message}</p>
                      {data.customerName && (
                        <p className="text-sm text-red-800 mt-1">Customer: {data.customerName}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="ml-4 text-red-700 hover:text-red-900 flex-shrink-0"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ), {
                duration: 10000,
                style: {
                  background: '#FEE2E2',
                  border: '2px solid #EF4444',
                  padding: '16px',
                  minWidth: '350px',
                  maxWidth: '500px',
                },
              });
            }
          } catch (error) {
            console.error('❌ Error parsing notification:', error);
          }
        };

        eventSource.onerror = (error) => {
          console.error('❌ Notification stream error:', error);
          console.error('❌ Error details:', {
            readyState: eventSource.readyState,
            readyStateText: ['CONNECTING', 'OPEN', 'CLOSED'][eventSource.readyState],
            url: eventSource.url,
            type: error.type,
            target: error.target,
            eventPhase: error.eventPhase,
            isTrusted: error.isTrusted,
            timeStamp: error.timeStamp
          });

          setIsConnected(false);

          // If connection is closed (readyState 2), don't try to close again
          const isClosed = eventSource.readyState === EventSource.CLOSED;

          // Check if this is a token expiration issue
          const tokenExpiration = getTokenExpiration(token);
          const now = Date.now();
          const isTokenExpired = tokenExpiration && tokenExpiration < now;

          if (isTokenExpired) {
            console.error('❌ Token has expired. User needs to re-authenticate.');
            toast.error('Your session has expired. Please log in again.', {
              duration: 10000,
            });
            if (!isClosed) eventSource.close();
            return;
          }

          // Check if this is a 401/403 error (authentication failure)
          // EventSource doesn't expose status codes, but we can infer from immediate closure
          if (isClosed && reconnectAttemptsRef.current === 0) {
            console.error('❌ Connection closed immediately - likely authentication error');
            toast.error('Failed to connect to notification system. Please try refreshing the page.', {
              duration: 10000,
            });
            return;
          }

          // Attempt to reconnect with exponential backoff
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
            console.log(`🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})...`);
            console.log(`🔄 Token expires in ${tokenExpiration ? Math.round((tokenExpiration - now) / 1000 / 60) : 'unknown'} minutes`);

            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttemptsRef.current += 1;
              connectToNotifications();
            }, delay);
          } else {
            console.error('❌ Max reconnection attempts reached. Giving up.');
            toast.error('Lost connection to notification system. Please refresh the page.', {
              duration: 10000,
            });
          }

          if (!isClosed) eventSource.close();
        };

        eventSourceRef.current = eventSource;

        // Set up automatic reconnection before token expires
        const tokenExpiration = getTokenExpiration(token);
        if (tokenExpiration) {
          const now = Date.now();
          const timeUntilExpiry = tokenExpiration - now;
          const refreshTime = timeUntilExpiry - (5 * 60 * 1000); // 5 minutes before expiry

          if (refreshTime > 0) {
            console.log(`🔔 Token refresh scheduled in ${Math.round(refreshTime / 1000 / 60)} minutes`);
            const refreshTimer = setTimeout(() => {
              console.log('🔄 Token about to expire, refreshing SSE connection...');
              eventSource.close();
              // The useEffect will re-run with the new token from context
              window.location.reload(); // Simple solution: reload to get fresh token
            }, refreshTime);

            // Store timer for cleanup
            reconnectTimeoutRef.current = refreshTimer;
          }
        }
      } catch (error) {
        console.error('❌ Error connecting to notifications:', error);
        setIsConnected(false);
      }
    };

    const cleanup = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (eventSourceRef.current) {
        console.log('🔌 Closing notification stream');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
    };

    connectToNotifications();

    // Cleanup on unmount
    return cleanup;
  }, [isAuthenticated, token]);

  const clearNotifications = () => {
    setNotifications([]);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  return {
    notifications,
    isConnected,
    clearNotifications,
    removeNotification,
  };
};
